// ABOUTME: REST hook for single offer detail and mutations (create, update, transition, etc.).
// ABOUTME: Provides CRUD operations against the /api/cpq endpoints.

import SnackBarUtils from '@src/components/shared/SnackBarUtils';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

import { LinePricingWarning } from '../components/OfferLineItems/pricingWarning';
import { ChainStep, LineItemSection } from '../types';
import { apiFetch } from '../utils/apiFetch';
import { BomSolveError } from '../utils/bomSolveErrors';
import { EpicorErrors, formatEpicorErrors } from '../utils/epicorErrors';

export { ApiError } from '../utils/apiFetch';

export interface OfferData {
    offerId: string;
    offerNumber: string;
}

/** The verdict of a node's schema `nodeReadonly` rule, evaluated by the server on the offer and on each line. */
export interface NodeReadonlyStateView {
    locked: boolean;
    excludedFields: string[];
    message?: string | null;
}

/**
 * The node fields an offer or order detail page reads besides its contents: the OFFER and ORDER
 * schemas carry no authoring dates, so the sidebar shows the node's own event-sourced timestamps
 * and the user recorded on the last projected change.
 */
export interface CpqDocumentNodeView {
    code?: string;
    listName?: string;
    createdAt?: number;
    updatedAt?: number;
    metadata?: { updatedBy?: string };
    readonlyState?: NodeReadonlyStateView;
}

export interface OfferDetail {
    offer: Record<string, unknown>;
    lineItems: Record<string, unknown>[];
    sections: Record<string, unknown>[];
    /** The line items grouped by catalog category; null when the tenant does not group them. */
    lineItemSections?: LineItemSection[] | null;
}

/**
 * Hook for offer CRUD operations via REST API.
 */
export function useOffer() {
    const { formatMessage } = useIntl();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const createOffer = useCallback(
        async (payload: { title: string; customerName: string; customerEmail?: string }) => {
            setLoading(true);
            setError(null);
            try {
                const result = await apiFetch<OfferData>('/api/cpq/offers', {
                    method: 'POST',
                    body: JSON.stringify(payload),
                });
                return result;
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to create offer';
                setError(message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const getOffer = useCallback(async (offerId: string) => {
        setLoading(true);
        setError(null);
        try {
            const result = await apiFetch<OfferDetail>(`/api/cpq/offers/${offerId}`);
            return result;
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to load offer';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateOffer = useCallback(async (offerId: string, payload: Record<string, unknown>) => {
        setLoading(true);
        setError(null);
        try {
            await apiFetch(`/api/cpq/offers/${offerId}`, {
                method: 'PUT',
                body: JSON.stringify(payload),
            });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to update offer';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const transitionStatus = useCallback(async (offerId: string, targetStatus: string) => {
        setLoading(true);
        setError(null);
        try {
            const result = await apiFetch<{
                previousStatus: string;
                newStatus: string;
                // `orderId` is set by convert-to-order; `bomSolveErrors` (Stuart-Rush finalize) lists
                // the configured lines sent to Epicor without their BOM.
                actionResult?: {
                    orderId?: string;
                    orderNumber?: string;
                    bomSolveErrors?: BomSolveError[];
                } | null;
            }>(`/api/cpq/offers/${offerId}/transition`, {
                method: 'POST',
                body: JSON.stringify({ targetStatus }),
            });
            return result;
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to transition';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const duplicateOffer = useCallback(async (offerId: string) => {
        setLoading(true);
        setError(null);
        try {
            const result = await apiFetch<OfferData>(`/api/cpq/offers/${offerId}/duplicate`, {
                method: 'POST',
            });
            return result;
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to duplicate';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const sendOffer = useCallback(async (offerId: string) => {
        setLoading(true);
        setError(null);
        try {
            await apiFetch(`/api/cpq/offers/${offerId}/send`, { method: 'POST' });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to send';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const addLineItem = useCallback(
        async (
            offerId: string,
            payload: {
                productId?: string;
                quantity: number;
                sectionId?: string;
                configurationJson?: string;
                productName?: string;
                unitPrice?: number;
                compatibleOf?: string;
                // Ordered configuration chain; its last step must match configurationJson.
                chain?: ChainStep[];
            },
        ) => {
            setLoading(true);
            setError(null);
            try {
                // `warning` is set when the item was added without a price because its
                // configuration could not be priced. `epicorErrors` (Stuart-Rush only) is set when
                // the Epicor discount / availability could not be read.
                const result = await apiFetch<{
                    lineItemId: string;
                    warning?: LinePricingWarning;
                    epicorErrors?: EpicorErrors;
                }>(`/api/cpq/offers/${offerId}/line-items`, {
                    method: 'POST',
                    body: JSON.stringify(payload),
                });
                // Raised here rather than at each caller: every add path goes through this hook,
                // and the line was added, so the seller must learn why it has no Epicor data.
                formatEpicorErrors(formatMessage, result?.epicorErrors).forEach(message =>
                    SnackBarUtils.error(message),
                );
                return result;
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to add line item';
                setError(message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [formatMessage],
    );

    const configureLineItem = useCallback(
        async (
            offerId: string,
            lineItemId: string,
            configurationJson: string,
            chain?: ChainStep[],
        ) => {
            setLoading(true);
            setError(null);
            try {
                // `warning` is set when the configuration was stored without a price. Older
                // responses carry no data at all, hence the undefined in the result type.
                return await apiFetch<{ warning?: LinePricingWarning } | undefined>(
                    `/api/cpq/offers/${offerId}/line-items/${lineItemId}/configure`,
                    {
                        method: 'POST',
                        body: JSON.stringify({ configurationJson, chain }),
                    },
                );
            } catch (err) {
                const message =
                    err instanceof Error ? err.message : 'Failed to configure line item';
                setError(message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const removeLineItem = useCallback(async (offerId: string, lineItemId: string) => {
        setLoading(true);
        setError(null);
        try {
            await apiFetch(`/api/cpq/offers/${offerId}/line-items/${lineItemId}`, {
                method: 'DELETE',
            });
        } catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to remove line item';
            setError(message);
            throw err;
        } finally {
            setLoading(false);
        }
    }, []);

    const updateLineItem = useCallback(
        async (
            offerId: string,
            lineItemId: string,
            payload: {
                quantity?: number;
                sectionId?: string | null;
                lineDiscountType?: 'percentage' | 'fixed' | 'none';
                lineDiscountValue?: number;
                isOptional?: boolean;
                notes?: string | null;
                productName?: string;
                unitPrice?: number;
            },
        ) => {
            setLoading(true);
            setError(null);
            try {
                await apiFetch(`/api/cpq/offers/${offerId}/line-items/${lineItemId}`, {
                    method: 'PUT',
                    body: JSON.stringify(payload),
                });
            } catch (err) {
                const message = err instanceof Error ? err.message : 'Failed to update line item';
                setError(message);
                throw err;
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    return {
        loading,
        error,
        createOffer,
        getOffer,
        updateOffer,
        transitionStatus,
        duplicateOffer,
        sendOffer,
        addLineItem,
        configureLineItem,
        removeLineItem,
        updateLineItem,
    };
}
